import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "flex-row w-full" }
const _hoisted_2 = { class: "page-header-tab-bar pb-4" }
const _hoisted_3 = { class: "flex gap-3 p-3" }
const _hoisted_4 = { class: "flex flex-wrap gap-3 flex-1 document-header" }
const _hoisted_5 = { class: "flex flex-row gap-1 align-items-center justify-content-start font-bold text-sm" }
const _hoisted_6 = {
  key: 1,
  class: "flex input-errors align-items-center h-full font-bold"
}
const _hoisted_7 = { class: "ml-auto" }
const _hoisted_8 = ["onClick", "textContent"]
const _hoisted_9 = ["textContent"]
const _hoisted_10 = ["textContent"]
const _hoisted_11 = ["textContent"]

import { ref, onMounted, computed } from 'vue';
import Calendar from 'primevue/calendar';
import AddOrganizationDocumentModal from '@/components/UploadModal/AddOrganizationDocumentModal.vue';
import OrganizationGeneralDocuments from './components/OrganizationGeneralDocuments.vue';
import OrganizationDataExportDocuments from './components/OrganizationDataExportDocuments.vue';
import AppTabMenu from '@/components/AppTabMenu';
import { useOrganizationDocumentStore, useBillingStore, useOrganizationStore } from '@/store';
import { can, useDateTime, useRouting } from '@/composables';
import { FilterEventInterface, OrganizationDocumentsFilterInterface } from '@/types';
import { PERMISSIONS } from '@/enum';


export default /*@__PURE__*/_defineComponent({
  __name: 'OrganizationDocuments',
  setup(__props) {

const defaultFilters: OrganizationDocumentsFilterInterface = {
  search: '',
  page: 0,
  start_at: '',
  end_at: '',
  per_page: 15,
};

const $routing = useRouting();
const organizationStore = useOrganizationStore();
const organizationDocumentStore = useOrganizationDocumentStore();
const billingStore = useBillingStore();
const documentModalOpen = ref(false);
const endDate = ref<Date>();
const filters = ref(defaultFilters);
const firstValue = ref(0);
const startDate = ref<Date>();
const active = ref(0);

const items = [
  {
    label: 'Billing Documents',
    visible: can([PERMISSIONS.ORGANIZATION_BILLING_DOCUMENTS_INDEX]),
    index: 0,
  },
  {
    label: 'Other Documents',
    index: 1,
  },
  {
    label: 'Data Export',
    visible:
      can([PERMISSIONS.ORGANIZATION_DOCUMENTS_GENERATE_DATA_EXPORT]) &&
      organizationStore.organization?.parent_id !== null,
    index: 2,
  },
];

/**
 * Check whether there are any filters defined by check for query params
 * within the route object.
 */
const hasFilter = computed<boolean>(() => {
  const { search, page, per_page } = $routing.route.query;
  return !!(search || page || per_page);
});

const isDataExportEnabled = computed<boolean>(
  () => active.value === 2 && organizationStore.organization?.parent_id !== null,
);

const openBillingDocumentUploader = () => (documentModalOpen.value = true);

onMounted(() => init());

const init = async () => {
  active.value = await getIndex();

  setDates(false);
  await defineFilters();

  if (!can([PERMISSIONS.ORGANIZATION_BILLING_DOCUMENTS_INDEX])) {
    active.value = 1;
  }

  if (filters.value.start_at && filters.value.end_at) {
    organizationDocumentStore.getBillingDocuments($routing.route.query);
  }
};

const getIndex = async () => {
  const index =
    $routing.route.query.index && organizationStore.organization?.parent_id !== null
      ? parseInt($routing.route.query.index as string)
      : 0;
  return index < items.length ? index : 0;
};

/**
 * set the default start/end dates.
 * replace them in defineFilters if a URL param
 * set the start date to four months back from today
 *  */
const setDates = (reset: boolean) => {
  const query = $routing.route.query;
  const currentDate = new Date();
  firstValue.value = 0;
  const startParam =
    query.start_at && !reset
      ? new Date(query.start_at as string)
      : new Date(currentDate.setDate(currentDate.getDate() - 84));
  const endParam = query.end_at && !reset ? new Date(query.end_at as string) : new Date();

  startParam.setDate(startParam.getDate() + 1);
  endParam.setDate(endParam.getDate() + 1);
  startDate.value = startParam;
  endDate.value = endParam;

  $routing.resetQueryParams({
    start_at: query.start_at || useDateTime().formatDateForApi(startParam),
    end_at: query.end_at || useDateTime().formatDateForApi(endParam),
    index: active.value || 0,
  });
};

/**
 * Define the filters based on the query params available on page load.
 */
const defineFilters = async () => {
  const query = $routing.route.query;

  filters.value = {
    search: query.search ? (query.search as string) : '',
    start_at: query.start_at ? (query.start_at as string) : '',
    end_at: query.end_at ? (query.end_at as string) : '',
    page: query.page ? parseInt(query.page as string) : 0,
    per_page: query.per_page ? parseInt(query.per_page as string) : defaultFilters.per_page,
  };

  if ($routing.route.query.page || $routing.route.query.per_page) {
    firstValue.value = (filters.value.page - 1) * filters.value.per_page;
  } else {
    firstValue.value = 0;
  }
};

const doToast = (summary: string, detail: string) => {
  organizationDocumentStore.doToast(detail, summary);
};

const doFailUnauthorized = (action: string) => {
  organizationDocumentStore.doToast(`User unauthorized to ${action} documents.`, 'Error');
};

/**
 * Watch for changes to the route object in order to execute a
 * new request for data.
 */
$routing.routeChangeWatcher(async () => {
  if (!can([PERMISSIONS.ORGANIZATION_BILLING_DOCUMENTS_INDEX]) || active.value !== 0) return;
  await organizationDocumentStore.getBillingDocuments($routing.route.query);
});

const onStartDateRangeChange = async (dateStart: Date) => {
  const start = useDateTime().formatDateForApi(dateStart);
  $routing.addQueryParams({ start_at: start, end_at: useDateTime().formatDateForApi(endDate.value) });
};

const onEndDateRangeChange = async (dateEnd: Date) => {
  const end = useDateTime().formatDateForApi(dateEnd);
  $routing.addQueryParams({ start_at: useDateTime().formatDateForApi(startDate.value), end_at: end });
};

const onPage = (event: FilterEventInterface) => {
  filters.value.page = event.page;
  filters.value.per_page = event.rows ? event.rows : 15;
  $routing.addQueryParams({ page: event.page + 1, per_page: event.rows });
};

const onSaveAdd = () => {
  if (can([PERMISSIONS.ORGANIZATION_BILLING_DOCUMENTS_STORE])) {
    organizationDocumentStore.getBillingDocuments($routing.route.query);
    doToast('Added', 'New Document has been added');
  } else {
    doFailUnauthorized('upload');
  }
};

const onCancelAdd = () => (documentModalOpen.value = false);

/**
 * Handle the ordering of the request data.
 */
const onSort = (event: FilterEventInterface) => {
  const order = event.sortOrder === 1 ? 'asc' : 'desc';
  $routing.addQueryParams({ order, order_by: event.sortField });
};

/**
 * Handle the filtering of results based on the provided search terms,
 * given 3 or or more characters have been provided.
 */
const onSearch = (search: string) => {
  const hasSearchTerm = search.length >= 3;

  if (hasSearchTerm) {
    $routing.addQueryParam('search', search);
    filters.value.search = search;
  } else {
    $routing.removeQueryParam('search');
  }
};

/**
 * clear all filters
 */
const onClearFilters = async () => {
  filters.value = { ...defaultFilters };
  firstValue.value = 0;
  setDates(true);
};

const datesInverted = computed(() => {
  return startDate.value && endDate.value ? startDate.value > endDate.value : false;
});
/**
 * download the invoice.
 * split the name to get the extension and if no extension use default xlsx
 * should handle pdf, zip and xlsx files
 * @param id
 * @param name
 */
const onDownloadInvoice = async (id: number, name: string) => {
  if (can([PERMISSIONS.ORGANIZATION_BILLING_DOCUMENTS_DOWNLOAD])) {
    const nameAr = name.split('.');
    const extension = nameAr[1] ? nameAr[1] : 'xlsx';
    await billingStore.downloadInvoice(id, nameAr[0], extension);
    doToast('Downloaded', 'Document successfully downloaded');
  } else {
    doFailUnauthorized('download');
  }
};

const onDeleteDocument: (id: number) => Promise<void> = async (id) => {
  if (can([PERMISSIONS.ORGANIZATION_BILLING_DOCUMENTS_DESTROY])) {
    await organizationDocumentStore.deleteBillingDocument(id);
    await organizationDocumentStore.getBillingDocuments($routing.route.query);
    doToast('Removed', 'Document has been removed');
  } else {
    doFailUnauthorized('delete');
  }
};

const handleTabMenuSelect = (index: number) => {
  active.value = index;
  $routing.addQueryParam('index', index.toString());
};

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!
  const _component_InputIcon = _resolveComponent("InputIcon")!
  const _component_InputText = _resolveComponent("InputText")!
  const _component_IconField = _resolveComponent("IconField")!
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_Column = _resolveComponent("Column")!
  const _component_DataTable = _resolveComponent("DataTable")!
  const _component_Card = _resolveComponent("Card")!
  const _directive_debounce = _resolveDirective("debounce")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_unref(AppTabMenu), {
        items: items,
        "active-index": active.value,
        onTab: handleTabMenuSelect
      }, null, 8, ["active-index"])
    ]),
    (active.value === 0)
      ? (_openBlock(), _createBlock(_component_Card, { key: 0 }, {
          header: _withCtx(() => [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", null, [
                  _createVNode(_component_IconField, { "icon-position": "left" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_InputIcon, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_Icon, {
                            name: "search",
                            class: "text-lg"
                          })
                        ]),
                        _: 1
                      }),
                      _withDirectives(_createVNode(_component_InputText, {
                        modelValue: filters.value.search,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filters.value.search) = $event)),
                        class: "w-full text-sm",
                        placeholder: "Search documents"
                      }, null, 8, ["modelValue"]), [
                        [_directive_debounce, onSearch, "600ms"]
                      ])
                    ]),
                    _: 1
                  })
                ]),
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_unref(Calendar), {
                    modelValue: startDate.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((startDate).value = $event)),
                    "show-icon": true,
                    "manual-input": false,
                    "icon-display": "input",
                    view: "month",
                    class: "w-full text-sm",
                    "date-format": "MM yy",
                    onDateSelect: onStartDateRangeChange
                  }, null, 8, ["modelValue"]),
                  _cache[4] || (_cache[4] = _createTextVNode(" to ", -1)),
                  _createVNode(_unref(Calendar), {
                    modelValue: endDate.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((endDate).value = $event)),
                    view: "month",
                    "show-icon": true,
                    "manual-input": false,
                    "icon-display": "input",
                    class: "w-full text-sm",
                    "date-format": "MM yy",
                    onDateSelect: onEndDateRangeChange
                  }, null, 8, ["modelValue"])
                ]),
                (hasFilter.value)
                  ? (_openBlock(), _createBlock(_component_AppButton, {
                      key: 0,
                      type: "secondary",
                      text: "Clear Filters",
                      "icon-left": "clear",
                      onClick: onClearFilters
                    }))
                  : _createCommentVNode("", true),
                (datesInverted.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, [...(_cache[5] || (_cache[5] = [
                      _createElementVNode("small", null, "End date must be after beginning date", -1)
                    ]))]))
                  : _createCommentVNode("", true)
              ])
            ]),
            _createElementVNode("div", _hoisted_7, [
              (_unref(can)([_unref(PERMISSIONS).ORGANIZATION_BILLING_DOCUMENTS_STORE]))
                ? (_openBlock(), _createBlock(_component_AppButton, {
                    key: 0,
                    type: "primary",
                    text: "Upload Document",
                    "icon-left": "cloud-upload",
                    onClick: openBillingDocumentUploader
                  }))
                : _createCommentVNode("", true)
            ])
          ]),
          content: _withCtx(() => [
            _createVNode(_component_DataTable, {
              filters: filters.value,
              "onUpdate:filters": _cache[3] || (_cache[3] = ($event: any) => ((filters).value = $event)),
              "filter-display": "menu",
              value: _unref(organizationDocumentStore).billingDocuments.data,
              paginator: true,
              "data-key": "id",
              "row-group-mode": "subheader",
              "group-rows-by": "invoice_id",
              "sort-field": "month_year",
              rows: filters.value.per_page,
              "total-records": _unref(organizationDocumentStore).billingDocuments.total,
              "row-hover": true,
              lazy: true,
              "loading-icon": "logo-spinner",
              loading: _unref(organizationDocumentStore).loading,
              "rows-per-page-options": [15, 50, 100],
              "paginator-template": "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown",
              "current-page-report-template": "Showing {first} to {last} of {totalRecords}",
              "responsive-layout": "scroll",
              first: firstValue.value,
              "sort-mode": "single",
              scrollable: "",
              onPage: onPage,
              onSort: onSort
            }, {
              empty: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                _createTextVNode("No documents found", -1)
              ]))]),
              groupheader: _withCtx((slotProps) => [
                _createElementVNode("span", {
                  textContent: _toDisplayString(slotProps.data.month_year)
                }, null, 8, _hoisted_11)
              ]),
              default: _withCtx(() => [
                (_unref(organizationDocumentStore).billingDocuments)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createVNode(_component_Column, {
                        field: "name",
                        header: "Title"
                      }, {
                        body: _withCtx((slotProps) => [
                          (_unref(can)([_unref(PERMISSIONS).ORGANIZATION_BILLING_DOCUMENTS_DOWNLOAD]))
                            ? (_openBlock(), _createElementBlock("span", {
                                key: 0,
                                class: "font-semibold cursor-pointer download",
                                onClick: ($event: any) => (onDownloadInvoice(slotProps.data.id, slotProps.data.name)),
                                textContent: _toDisplayString(slotProps.data.name)
                              }, null, 8, _hoisted_8))
                            : (_openBlock(), _createElementBlock("span", {
                                key: 1,
                                class: "font-semibold",
                                textContent: _toDisplayString(slotProps.data.name)
                              }, null, 8, _hoisted_9))
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_Column, {
                        field: "created_at",
                        header: "Date Created",
                        class: "justify-items-center"
                      }, {
                        body: _withCtx((slotProps) => [
                          _createElementVNode("span", {
                            class: "date-created",
                            textContent: _toDisplayString(_unref(useDateTime)().formatIntlDate(slotProps.data.created_at))
                          }, null, 8, _hoisted_10)
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_Column, {
                        "body-class": "justify-content-center",
                        "body-style": "font-size: .8725rem;",
                        "header-class": "justify-content-center",
                        header: "Actions"
                      }, {
                        body: _withCtx(({ data }) => [
                          (_unref(can)([_unref(PERMISSIONS).ORGANIZATION_BILLING_DOCUMENTS_DOWNLOAD]))
                            ? (_openBlock(), _createBlock(_component_AppButton, {
                                key: 0,
                                type: "text",
                                size: "large",
                                "icon-left": "cloud_download",
                                class: "text-600 cloud-download",
                                onClick: ($event: any) => (onDownloadInvoice(data.id, data.name))
                              }, null, 8, ["onClick"]))
                            : _createCommentVNode("", true),
                          (
                    _unref(can)([_unref(PERMISSIONS).ORGANIZATION_BILLING_DOCUMENTS_DESTROY]) &&
                    data.description == 'Manually uploaded document'
                  )
                            ? (_openBlock(), _createBlock(_component_AppButton, {
                                key: 1,
                                type: "text",
                                size: "large",
                                "icon-left": "delete",
                                class: "text-600",
                                onClick: ($event: any) => (onDeleteDocument(data.id))
                              }, null, 8, ["onClick"]))
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      })
                    ], 64))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["filters", "value", "rows", "total-records", "loading", "first"]),
            (documentModalOpen.value && _unref(can)([_unref(PERMISSIONS).ORGANIZATION_BILLING_DOCUMENTS_STORE]))
              ? (_openBlock(), _createBlock(AddOrganizationDocumentModal, {
                  key: 0,
                  title: "Upload Document",
                  billing: true,
                  onCancel: onCancelAdd,
                  onSave: onSaveAdd
                }))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (active.value === 1)
      ? (_openBlock(), _createBlock(OrganizationGeneralDocuments, { key: 1 }))
      : _createCommentVNode("", true),
    (isDataExportEnabled.value)
      ? (_openBlock(), _createBlock(OrganizationDataExportDocuments, { key: 2 }))
      : _createCommentVNode("", true)
  ]))
}
}

})